import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Stage, Layer } from 'react-konva';
import URLImage from './URLImage';
import assets, { resources } from './assets';
import CustomDialog from '../Overlays/CustomDialog';
import { CustomGridLayout } from '../Cards/CustomCard';
import { CustomDropDown, CustomInputDropDown } from '../Input/AllInputs';
import { capitalizeCamelCase } from '../../utils/commonFunctions';

export default function CustomLayoutPlan({ label, name, data, value, onChange, options = [], errorMessage, restrict = false, booked = [] }) {
    const stageRef = useRef();

    const [images, setImages] = useState([]);
    const [show, setShow] = useState(false);
    const [details, setDetails] = useState({
        setDirection: 'COLUMN',
        layoutPlan: { value: 10, type: 'ROW' },
    });

    const canvasWidth = 3000;
    const canvasHeight = 2000;
    const { setDirection, layoutPlan } = details;
    const { value: noOfItems, type: itemsDirection } = layoutPlan;

    const _value = value || data?.[name];

    useEffect(() => {
        if (_value.length) {
            if (JSON.stringify(_value) !== JSON.stringify(images)) {
                setImages(_value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_value]);

    useEffect(() => {
        if (!_value.length) {
            generateLayout();
        }
    }, [options]);

    function generateLayout() {
        setImages([]);
        let x = 100;
        let y = 100;
        options.forEach((item) => {
            let { offsetX, offsetY, shortName, name } = resources[item.resource];
            let _arr = [];
            let _availableQuantity = item.availableQuantity;

            let _y = y;
            let _x = x;

            for (let a = 1; a <= _availableQuantity; a++) {
                _arr.push({ name, x: _x, y: _y, shortName: `${shortName}-${a}` });

                if (itemsDirection === 'ROW') {
                    _x += offsetX;
                } else {
                    _y += offsetY;
                }
                if (a % noOfItems === 0 && _availableQuantity > a) {
                    if (itemsDirection === 'ROW') {
                        _y += offsetY;
                        _x = x;
                    } else {
                        _x += offsetX;
                        _y = y;
                    }
                }
            }
            setImages((prev) => [...prev, ..._arr]);
            let _remainder = _availableQuantity % noOfItems;
            let _emptySlots = noOfItems - _remainder;

            if (setDirection === 'ROW' && itemsDirection === 'ROW') {
                x = _x + 30 + _emptySlots * offsetX;
            } else if (setDirection === 'COLUMN' && itemsDirection === 'COLUMN') {
                y = _y + 30 + _emptySlots * offsetY;
            } else if (setDirection === 'ROW' && itemsDirection === 'COLUMN') {
                x = _x + 30 + offsetX;
            } else if (setDirection === 'COLUMN' && itemsDirection === 'ROW') {
                y = _y + 30 + offsetY;
            }
        });
        setImages((prev) => {
            onChange && onChange({ name, value: prev });
            return prev;
        });
    }

    const dragBoundFunc = (pos) => {
        let margin = 20;
        let newX = pos.x;
        let newY = pos.y;

        // Calculate the boundaries for the x and y coordinates
        const maxX = canvasWidth;
        const maxY = canvasHeight;

        // Check if newX is within the allowed boundaries
        if (newX < 0) newX = margin;
        if (newX > maxX) newX = maxX - margin;

        // Check if newY is within the allowed boundaries
        if (newY < 0) newY = margin;
        if (newY > maxY) newY = maxY - margin;

        return {
            x: newX,
            y: newY,
        };
    };

    const onHide = () => {
        setShow(false);
    };
    const onShow = () => {
        setShow(true);
    };

    const onSave = () => {
        generateLayout();
        onHide();
    };

    const handleChange = ({ name, value }) => {
        setDetails((prev) => ({ ...prev, [name]: value }));
    };

    const onDragEnd = (e, i) => {
        let newX = e.target.x();
        let newY = e.target.y();
        console.log({ newX, newY, i });

        const updated = [...images];
        updated[i]['x'] = newX;
        updated[i]['y'] = newY;
        setImages(updated);
        onChange && onChange({ name, value: updated });
    };

    let _images = useMemo(
        () => images.map((item) => ({ ...item, src: resources[item.name]['src'], isBooked: booked.includes(item.shortName) })),
        [images, booked],
    );
    console.log('_images==>', _images);

    return (
        <>
            <CustomDialog visible={show} onHide={onHide} onSave={onSave} title="Select Layout">
                <CustomGridLayout>
                    <CustomDropDown
                        col={12}
                        name="setDirection"
                        options={[
                            { name: 'Rows', value: 'COLUMN' },
                            { name: 'Columns', value: 'ROW' },
                        ]}
                        data={details}
                        onChange={handleChange}
                        required
                    />
                    <CustomInputDropDown
                        col={12}
                        name="layoutPlan"
                        options={[
                            { name: 'Rows', value: 'COLUMN' },
                            { name: 'Columns', value: 'ROW' },
                        ]}
                        data={details}
                        onChange={handleChange}
                        required
                    />
                </CustomGridLayout>
            </CustomDialog>
            <div className="col-12">
                <div htmlFor={name} className="text-sm font-semibold flex justify-content-between">
                    <div className="mb-1">{label ? label : capitalizeCamelCase(name || '')}</div>
                    {!restrict && <i className="pi pi-th-large cursor-pointer" onClick={onShow}></i>}
                </div>
                <div className="layout-plan">
                    <div>
                        <Stage width={canvasWidth} height={canvasHeight} ref={stageRef}>
                            <Layer>
                                {_images.map((image, i) => {
                                    return (
                                        <URLImage
                                            draggable={!restrict}
                                            key={i}
                                            onDragEnd={(e) => onDragEnd(e, i)}
                                            image={image}
                                            dragBoundFunc={dragBoundFunc}
                                        />
                                    );
                                })}
                            </Layer>
                        </Stage>
                    </div>
                </div>

                {errorMessage || data?.formErrors?.[name] ? (
                    <small className="p-error" id="error-element">
                        {errorMessage || data?.formErrors?.[name]}
                    </small>
                ) : null}
            </div>
        </>
    );
}
